import { useCallback } from 'react';
import { View } from 'react-native';
import { CreateResponsiveStyle, DEVICE_SIZES, minSize } from 'rn-responsive-styles';
import { useTranslation } from 'react-i18next';
import {
	Toast,
	Button,
	DefaultLayout,
	EditProfileForm,
	EditProfileFormSubmitValues,
	LoadingOverlay,
	PletlyLogo,
	RenderActionsProps,
	Text,
	WebCard,
	preparePhotoAsset,
	showApolloErrorToUser,
	spacing,
} from '@pletly/ui';

import { cleanInfoItems } from '@pletly/shared';

import { RootStackScreenProps } from '../../../../navigation/types';
import { useOrganisationMemberQuery } from '../../components/__generated__/organisationMember.generated';
import { useUpdateCareRecipientMemberMutation } from './__generated__/updateCareRecipientMember.mutation.generated';
import { withTranslation } from '../../../../locales/hoc';

export const FillCareRecipientInformation = withTranslation(
	({
		navigation: { goBack, replace },
		route: {
			params: { memberId, organisationUuid },
		},
	}: Props) => {
		const { t } = useTranslation();
		const { data, loading } = useOrganisationMemberQuery({ variables: { memberId } });
		const [updateCareRecipientMember] = useUpdateCareRecipientMemberMutation();

		const styles = useStyles();

		const handleSubmit = useCallback(
			async ({ inputs, newPhotoAsset, additionalInfoItems }: EditProfileFormSubmitValues) => {
				const { email: _email, ...careRecipientInputs } = inputs;
				const photoMediaFile = await preparePhotoAsset(newPhotoAsset);
				try {
					await updateCareRecipientMember({
						variables: {
							data: {
								memberId,
								organisationUuid,
								...careRecipientInputs,
								mediaFile: photoMediaFile,
								criticalInfo: cleanInfoItems(additionalInfoItems?.critical),
								negativeInfo: cleanInfoItems(additionalInfoItems?.negative),
								positiveInfo: cleanInfoItems(additionalInfoItems?.positive),
							},
						},
					});

					Toast.show({ text1: t('toasts.success.profile-saved'), type: 'success' });
					replace('FillCareRecipientInformationConfirmation', { memberId, organisationUuid });
				} catch (error) {
					console.error(error);
					showApolloErrorToUser(error as Error);
				}
			},
			[memberId, organisationUuid, updateCareRecipientMember, replace, t],
		);

		const renderActions = useCallback(
			({ canSubmit, submit }: RenderActionsProps) => (
				<View style={styles.actions}>
					<Button
						color="primary"
						variant="filled"
						disabled={!canSubmit}
						onPress={submit}
						style={styles.actionBtn}
					>
						{t('common.save')}
					</Button>

					<Button color="primary" variant="outline" onPress={goBack} style={styles.actionBtn}>
						{t('common.back')}
					</Button>
				</View>
			),
			[goBack, styles, t],
		);

		if (loading) {
			return <LoadingOverlay show />;
		}

		if (!data) {
			return null;
		}

		const { organisationMember } = data;

		return (
			<DefaultLayout style={styles.root}>
				<WebCard style={styles.card}>
					<PletlyLogo width={72} height={56} viewBox="0 0 158 123" style={styles.logo} />
					<Text category="h1" style={styles.title}>
						{t('fill-care-recipient-information.title')}
					</Text>
					<EditProfileForm
						renderActions={renderActions}
						user={organisationMember.user}
						isCareReceiver
						onSubmit={handleSubmit}
					/>
				</WebCard>
			</DefaultLayout>
		);
	},
);

const useStyles = CreateResponsiveStyle(
	{
		root: {
			flexDirection: 'row',
		},
		card: {
			marginHorizontal: 'auto',
			marginTop: spacing(3),
			padding: spacing(4),
		},
		logo: {
			marginHorizontal: 'auto',
		},
		title: {
			marginTop: spacing(4),
			marginBottom: spacing(4),
		},
		actions: {
			flexDirection: 'column',
			gap: spacing(4),
		},
		actionBtn: {
			flex: 1,
		},
	},
	{
		[minSize(DEVICE_SIZES.MD)]: {
			card: {
				maxWidth: 843,
			},
			actions: {
				flexDirection: 'row-reverse',
			},
		},
		[minSize(DEVICE_SIZES.LG)]: {
			card: {
				marginTop: spacing(6),
				padding: spacing(10),
			},
			title: {
				marginTop: spacing(10),
			},
		},
	},
);

type Props = RootStackScreenProps<'FillCareRecipientInformation'>;

export type FillCareRecipientInformationScreenParams = {
	organisationUuid: string;
	memberId: string;
};
